import React from "react";
import { Form, Row, Col, Button, Nav, ListGroup } from "react-bootstrap";
import { PacmanLoader } from "react-spinners";
import CreatableSelect from "react-select/creatable";
import { Link } from "react-router-dom";
import EditableCell from "containers/common/EditableCell";
import { GenericTable } from "components/GenericTable";
import "containers/common/List.css";
import "containers/common/Table.css";
import { PAGE_SIZES } from "utils/pageSizeConfig";
import { createElementIdFactory, createReusableTableElementIds } from "utils/elementIds";

import {
  createColumnHelper,
} from "@tanstack/react-table";

function EntityPropertyList({
  data,
  isLoading,
  isAuthenticated,
  controlType,
  propData,
  updateTableState,
  saveBrowseList,
  browseListsOptions,
  handleBrowseListChange,
  handleBrowseListInputChange,
  deleteBrowseList,
  selectedBrowseListOption,
  searchBrowseList,
}) {

  const dataSet = 'properties';

  // --- TanStack Table Setup ---
  const tableData = data || [];
  const columnHelper = createColumnHelper();
  const createId = React.useMemo(() => createElementIdFactory("entity-property-list"), []);
  const tableElementIds = React.useMemo(
    () => createReusableTableElementIds(createId, controlType || "table"),
    [createId, controlType]
  );

  const col_selected = columnHelper.accessor("selected", {
    id: "selected",
    header: "",
    cell: EditableCell,
    size: 60,
    enableSorting: false,
    meta: {
      style: { textAlign: "center" },
    },
  })
  const col_property_key = columnHelper.accessor("property_key", {
    id: "property_key",
    header: "Cityscope Reference",
    cell: (e) => (
      <Link
        to={{
          pathname: `/properties/${e.getValue()}`,
        }}
      >
        {e.getValue()}
      </Link>
    ),
    size: 75,
  })
  const col_building_name = columnHelper.accessor("building_name", {
    id: "building_name",
    header: "Building Name",
    cell: (info) => info.getValue(),
    meta: {
      style: { whiteSpace: "unset" },
    },
  })
  const col_main_address = columnHelper.accessor("main_address", {
    id: "main_address",
    header: "Main Address",
    cell: (info) => {
      const value = info.getValue();
      if (typeof value === "boolean") {
        return String(value);
      }

      return value ?? "";
    },
    size: 80,
    meta: {
      style: { whiteSpace: "unset" },
    },
  })
  const col_street_num_low = columnHelper.accessor("street_num_low", {
    id: "street_num_low",
    header: "St No. Low",
    cell: (info) => info.getValue(),
    size: 100,
  })
  const col_street_num_low_suffix = columnHelper.accessor("street_num_low_suffix", {
    id: "street_num_low_suffix",
    header: "St No. Low Suffix",
    cell: (info) => info.getValue(),
    size: 75,
  })
  const col_street_num_high = columnHelper.accessor("street_num_high", {
    id: "street_num_high",
    header: "St No. High",
    cell: (info) => info.getValue(),
    size: 70,
  })
  const col_street_num_high_suffix = columnHelper.accessor("street_num_high_suffix", {
    id: "street_num_high_suffix",
    header: "St No. High Suffix",
    cell: (info) => info.getValue(),
    size: 75,
  })
  const col_street_name = columnHelper.accessor("street_name", {
    id: "street_name",
    header: "St Name",
    cell: (info) => info.getValue(),
    meta: {
      style: { whiteSpace: "unset" },
    },
  })
  const col_suburb = columnHelper.accessor("suburb", {
    id: "suburb",
    header: "Suburb",
    cell: (info) => info.getValue(),
    meta: {
      style: { whiteSpace: "unset" },
    },
  })
  const col_state = columnHelper.accessor("state", {
    id: "state",
    header: "State",
    cell: (info) => info.getValue(),
    size: 75,
  })
  const col_postcode = columnHelper.accessor("postcode", {
    id: "postcode",
    header: "Postcode",
    cell: (info) => info.getValue(),
    size: 75,
  })
  const col_title_details = columnHelper.accessor("title_details", {
    id: "title_details",
    header: "Title",
    cell: (info) => info.getValue(),
    size: 300,
    meta: {
      style: { whiteSpace: "unset" },
    },
  })

  const columns = [
    col_selected,
    col_property_key,
    col_building_name,
    col_main_address,
    col_street_num_low,
    col_street_num_low_suffix,
    col_street_num_high,
    col_street_num_high_suffix,
    col_street_name,
    col_suburb,
    col_state,
    col_postcode,
    col_title_details,
  ];

  const columns1 = [
    col_property_key,
    col_building_name,
    col_main_address,
    col_street_num_low,
    col_street_num_low_suffix,
    col_street_num_high,
    col_street_num_high_suffix,
    col_street_name,
    col_suburb,
    col_state,
    col_postcode,
  ];

  function selectAll(event) {
    let data = [...propData];
    for (var i = 0; i < data.length; i++) {
      data[i].selected = true;
    }
    updateTableState(data, dataSet);
  }

  function deselectAll(event) {
    let data = [...propData];
    for (var i = 0; i < data.length; i++) {
      data[i].selected = false;
    }
    updateTableState(data, dataSet);
  }

  function renderList(data) {
    if (!data) {
      return null;
    } else {
      return [{}].concat(data).map((d, i) =>
        i !== 0 ? (
          <ListGroup.Item key={i} as="a" header={d.property_key}>
            {d.property_key + " " + d.street_name}
          </ListGroup.Item>
        ) : (
          ""
        )
      );
    }
  }

  function renderComponent() {
    const activeColumns = controlType === "table" ? columns : columns1;

    return (
      <div className="properties">
        {controlType === "table" && (
          <div>
            <Row>
              <Col className="text-left">
                <Nav className="justify-content-start">
                  <Nav.Item>
                    <Button variant="primary" type="button" size="sm" onClick={selectAll}>
                      Select All
                    </Button>{" "}
                  </Nav.Item>{" "}
                  <Nav.Item>
                    <Button variant="primary" type="button" size="sm" onClick={deselectAll}>
                      Deselect All
                    </Button>{" "}
                  </Nav.Item>{" "}
                  <Nav.Item>
                    <CreatableSelect
                      value={selectedBrowseListOption}
                      onChange={handleBrowseListChange}
                      options={browseListsOptions}
                      onCreateOption={handleBrowseListInputChange}
                      placeholder="Select a Browse Lists"
                      isClearable
                    />
                    <Form.Control
                      as="input"
                      type="text"
                      style={{
                        visibility: "hidden",
                        padding: "0px 0px",
                        "lineHeight": "0px",
                        "fontSize": "0px",
                        width: "300px",
                      }}
                    />
                  </Nav.Item>
                  <Nav.Item>
                    {" "}
                    <Button variant="primary" type="button" size="sm" onClick={searchBrowseList}>
                      Retrieve Browse List
                    </Button>{" "}
                  </Nav.Item>
                  <Nav.Item>
                    <Button variant="primary" type="button" size="sm" onClick={saveBrowseList}>
                      Add to Browse List
                    </Button>{" "}
                  </Nav.Item>
                  {selectedBrowseListOption && (
                    <Nav.Item>
                      <Button variant="danger" type="button" size="sm" onClick={deleteBrowseList}>
                        Delete Browse List
                      </Button>{" "}
                    </Nav.Item>
                  )}
                </Nav>
              </Col>
              <Col className="text-right" sm={4} md={3} lg={2}>
                <Link to={`/properties/new`}>
                  <Button variant="primary" type="button" size="sm">
                    Add New Property
                  </Button>
                </Link>
              </Col>
            </Row>
            <Row className="mb-2">
              <Col><b>{data.length} of {data[0]?.result_count || "0"} records returned.</b></Col>
            </Row>
          </div>
        )}

        <Row className="show-Container">
          <Col sm={12} md={12} lg={12}>
            {isLoading && (
              <div className="outer-div">
                <div className="sweet-loading inner-div">
                  <PacmanLoader
                    cssOverride={{ display: "block", margin: "0 auto" }}
                    size={20}
                    color={"#e03c31"}
                    loading={isLoading}
                  />
                </div>
              </div>
            )}
            {!isLoading && controlType === "table"
              ? <GenericTable
                section="property-list"
                columns={activeColumns}
                data={tableData}
                pageSize={PAGE_SIZES.propertySearch}
                elementIds={tableElementIds}
              />
              : controlType === "table2" || controlType === "table1"
                ? <GenericTable
                  section="property-list"
                  columns={activeColumns}
                  data={tableData}
                  pageSize={PAGE_SIZES.propertySearch}
                  elementIds={tableElementIds}
                />
                : renderList(tableData)}
          </Col>
        </Row>
      </div>
    );
  }
  return (
    <div className="PropertyList">
      {isAuthenticated ? renderComponent() : null}
    </div>
  );
}

export default EntityPropertyList;